// apps/client/src/pages/transactions/components/TransactionActivityAllocation.tsx
import {
  ProjectAccount,
  ResearchActivity,
  ResearchActivityType,
  TaxType,
  Transaction,
  TransactionSource,
} from '@avanzu/types';
import { AutoFixHigh, PlaylistAdd, PlaylistRemove } from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  Link as MuiLink,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useProject } from '../../../contexts/ProjectContext';
import CustomPercentageInput from './CustomPercentageInput';
import MagicWandButton from './MagicWandButton';
import PersonPercentIcon from './PersonPercentIcon';

interface TransactionActivityAllocationProps {
  transaction: Transaction;
  activities: ResearchActivity[];
  accounts: ProjectAccount[];
  /**
   * Called when the user confirms an allocation. When `options.applySalaryLoading`
   * is true the per-activity `allocations` map is ignored by the server — the
   * split is derived from the project's salary mix.
   */
  onConfirm: (
    transactionId: string,
    allocations: Record<string, number>,
    options?: { applySalaryLoading?: boolean }
  ) => void;
  isSaving?: boolean;
  disabled?: boolean;
  /**
   * True when the project has usable salary data (employees allocated with
   * payslips in the tax-year window). The salary-loading toggle is disabled
   * when this is false, with an explanatory tooltip.
   */
  hasSalaryData?: boolean;
  /**
   * Precomputed preview of the salary-loading split for this project. Shared
   * by every row (since the mix is project-scoped, not transaction-scoped) and
   * fetched once on page load. When salary loading is toggled on for a row,
   * its per-activity inputs render these percentages (disabled).
   *
   * - `totalPct`: per-row total R&D % (sum of activityPct values).
   * - `activityPct`: percentage allocated to each activity (0..100 scale).
   */
  salaryPreview?: {
    totalPct: number;
    activityPct: Record<string, number>;
  } | null;
}

const TransactionActivityAllocation: React.FC<TransactionActivityAllocationProps> = ({
  transaction,
  activities,
  accounts,
  onConfirm,
  isSaving = false,
  disabled = false,
  hasSalaryData = false,
  salaryPreview = null,
}) => {
  const { currentProjectId } = useProject();
  const [allocations, setAllocations] = useState<Record<string, number>>({});
  // Salary-loading mode for this row. When on, the per-activity inputs are
  // disabled (showing the "auto" placeholder) and the Confirm button submits
  // with applySalaryLoading=true; the server derives the actual split.
  const [salaryLoadingOn, setSalaryLoadingOn] = useState(false);

  const totalAllocated = Object.values(allocations).reduce((sum, val) => sum + val, 0);
  const isFullyAllocated = totalAllocated === 100;
  const isZeroAllocated = totalAllocated === 0;
  const isValidAllocation = totalAllocated > 0 && totalAllocated <= 100;

  // Find account that matches this transaction's account code and has allocation rules
  const matchingAccount = accounts.find((acc) => acc.code === transaction.accountCode);
  // Check if account has a rule: either excluded or has allocations
  const hasAccountRule =
    matchingAccount &&
    (matchingAccount.isExcluded ||
      (matchingAccount.allocations && matchingAccount.allocations.length > 0));

  // Pre-fill allocations if there's a rule for this account
  useEffect(() => {
    if (hasAccountRule && matchingAccount) {
      const ruleAllocations: Record<string, number> = {};
      matchingAccount.allocations.forEach((allocation) => {
        if (allocation.allocation > 0) {
          ruleAllocations[allocation.activityId] = allocation.allocation;
        }
      });
      setAllocations(ruleAllocations);
    }
  }, [transaction.accountCode, matchingAccount, hasAccountRule]);

  // Sort activities: CORE first, then SUPPORTING
  const sortedActivities = [...activities].sort((a, b) => {
    if (a.type === ResearchActivityType.CORE && b.type === ResearchActivityType.SUPPORTING)
      return -1;
    if (a.type === ResearchActivityType.SUPPORTING && b.type === ResearchActivityType.CORE)
      return 1;
    return a.sortOrder - b.sortOrder;
  });

  const handlePercentageChange = (activityId: string, value: string) => {
    const percentage = parseFloat(value) || 0;
    if (percentage >= 0 && percentage <= 100) {
      setAllocations((prev) => ({
        ...prev,
        [activityId]: percentage,
      }));
    }
  };

  const handleConfirm = () => {
    if (salaryLoadingOn) {
      // Per-activity values are ignored server-side in this mode — we still
      // pass an empty map for clarity. The server derives the split from the
      // project's salary mix and writes the materialised rows.
      onConfirm(transaction.id, {}, { applySalaryLoading: true });
      return;
    }
    onConfirm(transaction.id, allocations);
  };

  const handleToggleSalaryLoading = () => {
    if (!hasSalaryData) return;
    setSalaryLoadingOn((prev) => !prev);
  };

  const handleExclude = () => {
    // exclude transaction from R&D allocation
    // set allocationStatus to EXCLUDED and clear allocations
    onConfirm(transaction.id, {}); // empty allocations means exclude
  };

  const handleAutoAllocate = () => {
    // Find account rule and apply auto-allocation, then auto-submit
    if (hasAccountRule && matchingAccount) {
      // Check if this is an exclusion rule
      if (matchingAccount.isExcluded) {
        // Auto-submit as excluded
        onConfirm(transaction.id, {});
      } else {
        // Build allocations from rule and auto-submit
        const newAllocations: Record<string, number> = {};
        matchingAccount.allocations.forEach((allocation) => {
          if (allocation.allocation > 0) {
            newAllocations[allocation.activityId] = allocation.allocation;
          }
        });
        onConfirm(transaction.id, newAllocations);
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <TableRow
      sx={{
        '&:hover': { bgcolor: hasAccountRule ? 'success.light' : 'action.hover' },
        ...(hasAccountRule ? { bgcolor: 'success.light' } : {}),
      }}
    >
      {/* Transaction Details
       *   Line 1: Contact name (the company)
       *   Line 2: Transaction description
       *   Line 3: Date  •  [SOURCE chip] [TAX chip]  •  account code link
       */}
      <TableCell sx={{ width: '350px', minWidth: '350px' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Stack>
            <Typography variant="body2" color="text.secondary">
              {transaction.contactName || 'No contact'}
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
              {transaction.description || 'No description'}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              component="span"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}
            >
              <span>{formatDate(new Date(transaction.date))}</span>
              <span>•</span>
              <TransactionSourceChip source={transaction.source} />
              <TaxTypeChip taxType={transaction.taxType} />
              <span>•</span>
              <CodeRuleLink
                accountCode={transaction.accountCode}
                currentProjectId={currentProjectId}
              />
            </Typography>
          </Stack>
        </Stack>
      </TableCell>

      {/* Amount */}
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {formatCurrency(transaction.totalAmount - (transaction.taxAmount || 0))}
        </Typography>
      </TableCell>

      {/* Actions - Condensed like Accounts */}
      <TableCell
        sx={{
          width: '120px',
          minWidth: '120px',
          textAlign: 'center',
          verticalAlign: 'middle',
          padding: 1,
        }}
      >
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={0}>
          <Tooltip
            title={`Auto-apply and submit based on account rule for ${transaction.accountCode}`}
          >
            <span>
              <MagicWandButton
                onClick={handleAutoAllocate}
                visible={hasAccountRule || false}
                disabled={disabled}
              />
            </span>
          </Tooltip>

          <Tooltip
            title={
              !hasSalaryData
                ? 'No salary data available for this project yet'
                : salaryLoadingOn
                  ? 'Salary loading active — click to disable'
                  : 'Toggle: Apply loading based on salary allocated to project'
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={handleToggleSalaryLoading}
                disabled={!hasSalaryData || isSaving || disabled}
                color={salaryLoadingOn ? 'primary' : 'secondary'}
                sx={
                  salaryLoadingOn
                    ? { bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.100' } }
                    : undefined
                }
              >
                <PersonPercentIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Exclude: Not research and development related">
            <span>
              <IconButton size="small" color={'error'} onClick={handleExclude} disabled={disabled}>
                <PlaylistRemove fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip
            title={
              salaryLoadingOn
                ? 'Allocate via salary loading'
                : 'Allocate transaction to research activities'
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={
                  (salaryLoadingOn ? false : !isValidAllocation) || isSaving || disabled
                }
                color={'success'}
                onClick={handleConfirm}
              >
                <PlaylistAdd fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>

      {/* Total Percentage */}
      <TableCell
        sx={{
          width: '60px',
          minWidth: '60px',
          textAlign: 'center',
          verticalAlign: 'middle',
          fontWeight: 600,
        }}
      >
        {salaryLoadingOn ? (
          // Render the precomputed total — disabled but real. Styled in italic
          // primary so it's visually distinct from a user-entered total.
          <Typography
            variant="caption"
            color="primary.main"
            fontStyle="italic"
            fontWeight={500}
          >
            {salaryPreview ? `${salaryPreview.totalPct.toFixed(0)}%` : '—'}
          </Typography>
        ) : (
          <Typography
            variant="caption"
            color={
              isFullyAllocated ? 'success.main' : totalAllocated > 100 ? 'error' : 'text.secondary'
            }
            fontWeight={500}
          >
            {totalAllocated.toFixed(0)}%
          </Typography>
        )}
      </TableCell>

      {/* Activity Allocations */}
      {sortedActivities.map((activity) => (
        <TableCell
          key={activity.id}
          sx={{ width: '100px', minWidth: '100px', p: 1, textAlign: 'center' }}
        >
          <CustomPercentageInput
            // When loading is on, show the precomputed percentage from the
            // salary mix — disabled but real, so the user sees the actual
            // derived split without an "auto" placeholder. Falls back to 0
            // when no preview is available (defensive; toggle is gated on
            // hasSalaryData which implies a preview exists).
            value={
              salaryLoadingOn
                ? (salaryPreview?.activityPct[activity.id] ?? 0)
                : (allocations[activity.id] || 0)
            }
            onChange={(value) => handlePercentageChange(activity.id, value)}
            width={80}
            disabled={disabled || salaryLoadingOn}
          />
        </TableCell>
      ))}
    </TableRow>
  );
};

export default TransactionActivityAllocation;

/**
 * Visual marker for the row source — distinguishes:
 *   - BILL          : AP invoice from Xero's /Invoices (Type=ACCPAY)
 *   - SELF INVOICE  : self-referencing invoice journal (opt-in, separate pathway)
 *   - SPEND MONEY   : bank transaction from /BankTransactions
 *
 * All three are counted identically in reports and allocations — the chip is
 * purely a visual marker so users can tell at a glance which Xero pathway a
 * row came from.
 */
// Shared chip sx — tight horizontal padding and reduced height so the source
// and tax-type chips read as quiet metadata, not headline UI.
const META_CHIP_SX = {
  fontSize: '0.6rem',
  height: 16,
  '& .MuiChip-label': { px: 0.5 },
};

export const TransactionSourceChip: React.FC<{ source?: TransactionSource | string }> = ({
  source,
}) => {
  let label = 'SPEND MONEY';
  let color: 'default' | 'primary' | 'secondary' | 'warning' = 'primary';

  if (source === TransactionSource.BILL) {
    label = 'BILL';
    color = 'default';
  } else if (source === TransactionSource.SELF_INVOICE) {
    label = 'SELF INVOICE';
    color = 'secondary';
  }

  return <Chip label={label} size="small" color={color} sx={META_CHIP_SX} />;
};

export const TaxTypeChip: React.FC<{ taxType?: TaxType }> = ({ taxType }) => {
  switch (taxType) {
    case TaxType.INPUT:
      return <Chip label="GST" size="small" sx={META_CHIP_SX} />;
    case TaxType.OUTPUT:
      return <Chip label="Output Tax" size="small" sx={META_CHIP_SX} />;
    case TaxType.EXEMPTEXPENSES:
      return <Chip label="Tax Exempt" size="small" sx={META_CHIP_SX} />;
    case TaxType.GSTONIMPORTS:
      return <Chip label="GST on imports" size="small" sx={META_CHIP_SX} />;
    case TaxType.BASEXCLUDED:
      return <Chip label="BAS excluded" size="small" sx={META_CHIP_SX} />;
    default:
      return <Chip label={taxType} size="small" sx={META_CHIP_SX} />;
  }
};

const CodeRuleLink: React.FC<{
  accountCode: string;
  currentProjectId: string | null;
}> = ({ accountCode, currentProjectId }) => {
  return (
    <MuiLink
      component={Link}
      to={`/app/rules/${currentProjectId}?code=${accountCode}`}
      variant="caption"
      color="primary"
      underline="hover"
      sx={{
        display: 'inline-flex',
        minWidth: '35px',
        alignItems: 'center',
        gap: 0.25,
        '& .hover-icon': {
          opacity: 0,
          // transition: 'opacity 0.2s', // causes movement in table
        },
        '&:hover .hover-icon': {
          opacity: 1,
        },
      }}
    >
      {accountCode}
      <AutoFixHigh
        className="hover-icon"
        sx={{
          fontSize: '0.75rem',
          color: 'primary.main',
        }}
      />
    </MuiLink>
  );
};
